// Update this date when the policy content changes — triggers re-acceptance for all users.
export const PRIVACY_POLICY_ISSUED_DATE = "2026-06-05";

export const PRIVACY_POLICY_MD = `---
title: Privacy Policy
issuedDate: "${PRIVACY_POLICY_ISSUED_DATE}"
---

# Erode AI Privacy Policy

**Last Updated:** June 5, 2026

Erode AI Inc. provides forecasting and analytics services. This policy explains how we collect, use, and protect your information. References to "you" apply to both the organization subscribing to our services and the individual authorized users acting on its behalf.

Our services are offered to customers in Canada and the United States. We comply with Canadian privacy laws (PIPEDA, Quebec's Law 25, and CASL) and applicable US state privacy laws, including California's CCPA/CPRA.

---

## 1. What Information We Collect

**Account Information:** Your name, email, password, company details, and billing information.

**Customer Data:** The content you provide to our platform — including datasets, files, prompts, and connected data sources — and the forecasts, analyses, and other outputs our platform generates from it.

**User Profiles and Memory:** We retain context from your interactions, including preferences and prior prompts, to customize and improve the responses our platform provides to you.

**Technical Information:** IP address, browser type, device information, how you use our platform, and system logs.

**Cookies and Tracking:** We use cookies for essential functionality, analytics, and payment processing (Stripe). Essential cookies cannot be disabled. We use Google Analytics and PostHog for analytics purposes; both are configured to collect anonymized, non-identifiable data only. Functional cookies can be controlled through your browser settings.

---

## 2. How We Use Your Information

We use your information to deliver and improve our services, manage your account and process payments, keep the platform secure, provide customer support, send service updates, send marketing communications (only with your consent), and meet legal requirements. We collect this information directly from you when you create an account or use our platform, automatically through your interactions with our services, and through third-party integrations you choose to connect.

**AI Model Training:** We may use aggregated and anonymized data to improve our AI models. We implement safeguards to ensure individual users and specific datasets cannot be identified. If we need to use identifiable Customer Data for model training, we'll obtain your explicit consent first.

---

## 3. Who We Share Information With

We don't sell your data.

### Service Providers

We work with trusted partners who help us run our services:

- Anthropic (API, language model services)
- AWS (cloud hosting in Canada and US)
- Google Analytics (platform usage analytics)
- Google Cloud (cloud hosting in Canada and US)
- Google Gemini (API, language model services)
- OpenAI (API, language model services)
- PostHog (product analytics)
- Stripe (payment processing)

We may engage additional service providers from time to time. For providers that process Customer Data, we'll provide reasonable advance notice. For administrative and internal tools that don't access Customer Data, we may add providers without notice.

### Legal Requirements

We may share information when required by law, to respond to legal processes, or to protect our rights and safety.

### Business Transfers

If we are involved in a merger, acquisition, or sale, your information may be transferred to the new entity, subject to this Policy.

### Third-Party Integrations

When you connect third-party tools to our platform, we'll share data as needed for those integrations to work. We are not responsible for the privacy practices of third-party integrations you choose to connect.

---

## 4. Where We Store Your Data

Your data may be stored and processed in Canada and the United States. For Quebec users, we conduct Privacy Impact Assessments as required by Law 25 before transferring data outside Quebec.

If you need your data stored only in Canada or the US, contact privacy@erode.ai.

---

## 5. How Long We Keep Your Data

**Your uploaded data:** Kept while your account is active, plus 12 months after closure for reactivation purposes, then permanently deleted.

**Chat histories, user profiles, and memory:** Retained for the duration of your account. Upon account closure, these are deleted along with your uploaded data after the 12-month reactivation window.

**Account and billing records:** Retained for 7 years after account closure to meet tax and legal requirements.

**System logs:** Kept for up to 36 months for security and troubleshooting.

You can request earlier deletion by emailing privacy@erode.ai, subject to legal retention requirements.

---

## 6. How We Protect Your Data

Your data is encrypted in transit and at rest. We use role-based access controls and monitor our platform around the clock. If there's a data breach affecting you, we'll notify you without undue delay.

---

## 7. Marketing Communications

We follow CASL. We'll only send marketing emails if you've given consent or we have an existing business relationship with you. Every marketing email includes an unsubscribe link — if you opt out, we'll stop sending marketing within 10 business days. You'll still receive necessary service emails such as receipts, security alerts, and account notifications.

---

## 8. Your Privacy Rights

Depending on where you live, you may have the right to access, correct, delete, or export your personal information, and to object to certain processing. Jurisdictions such as Quebec (Law 25) and California (CCPA/CPRA) may grant additional rights including the right to withdraw consent, opt out of data sales, or object to profiling and automated decision-making based on your data.

To exercise your rights, email privacy@erode.ai. We will respond within the timelines required by law.

---

## 9. Children's Privacy

Our services are not intended for children. If we discover we've inadvertently collected information from a child, we'll delete it. Contact privacy@erode.ai with concerns.

---

## 10. Changes to This Policy

If we make significant changes, we'll notify you by email or through a prominent notice on our platform with reasonable notice before the changes take effect. Continuing to use our services after that means you accept the updated policy.

---

## 11. Governing Law and Dispute Resolution

Unless otherwise required by applicable law, this Privacy Policy is governed by the laws of Ontario, Canada. Disputes will be subject to the exclusive jurisdiction of the courts of Ontario, Canada.

---

## 12. Contact Us

For all privacy inquiries (including Quebec Law 25 and CCPA/CPRA rights), contact the Privacy Officer, Erode AI Inc. at privacy@erode.ai. If you have a privacy complaint, we will acknowledge it promptly and work to resolve it in a timely manner. If you are not satisfied with our response, you may escalate to the relevant privacy regulator in your jurisdiction.

**Mailing address:**
Erode AI Inc.
3143 25 Sideroad
Rockwood, Ontario, N0B 2K0
Canada
`;
